import { useEffect, useMemo } from "react"
import { useStableContributorsCache } from "./use-stable-contributors-cache"
import { map, isEmpty } from "lodash"
import { LOADING, OK } from "helpers/data-statuses"
import { useIntradayPriceStream } from "connected-components/dashboard/hooks/use-intraday-price"
import { useIntradayStandalonePerformanceStream } from "connected-components/dashboard/hooks/use-intraday-standalone-performance"
import {
  buildIntradayFlatMap,
  buildIntradayStandaloneFlatMap,
} from "gatherers/contributors/intraday"
import { collectLeafModelProviderIds } from "helpers/contributors"
import { enrichMetricsWithIntradayAsOfTooltips } from "helpers/metrics"
import {
  buildContributionSuffixIndexByModelProviderId,
  getInstrumentSuffixForIntradayContributionPrefix,
  mergeFlatContributors,
  normalizeContributorKey,
  shouldAutoInvalidateIntradayContributionGathererOnPricingUpdate,
  shouldSubscribeToIntradayPriceTicks,
} from "../contributors-data-integrator.utils"

const EMPTY_ARRAY = []

// ISO datetime strings are appended as trailing asOf tokens by use-template-gatherers.
const ISO_DATETIME_PREFIX = /^\d{4}-\d{2}-\d{2}T/

function getIntradayContributionGathererModelId(gatherer) {
  return gatherer?.modelId || gatherer?.variables?.modelId
}

export function useIntradayContributorsEnhancement({
  nonIntradayGatherers,
  intradayGatherers,
  metrics,
  data,
  status,
  intradayContributionPerformanceLastUpdatedAt,
  isFetchingIntradayContributionQueries,
  isAnyQueryPending,
  isIntradayAnalyticsEnabled,
  instrumentsWithMetadata,
  queryClient,
}) {
  const intradayContributionQueryGatherers = useMemo(() => {
    return nonIntradayGatherers.filter((gatherer) =>
      getInstrumentSuffixForIntradayContributionPrefix(gatherer.prefix) != null,
    )
  }, [nonIntradayGatherers])
  const hasIntradayStreamingOrContributionGatherers = intradayGatherers.length > 0 || intradayContributionQueryGatherers.length > 0

  // Stable key identifies this logical query set independent of the asOf timestamp.
  // When the key changes (navigation to a different portfolio/instrument), cached data is discarded.
  const stableKey = useMemo(() => nonIntradayGatherers.map(g => {
    const flat = Array.isArray(g.key) ? g.key.flat(Infinity) : [g.key]
    const last = flat[flat.length - 1]
    const isAsOfToken = typeof last === "string" && ISO_DATETIME_PREFIX.test(last)
    const stableSegments = isAsOfToken ? flat.slice(0, -1) : flat
    return stableSegments.map(seg => JSON.stringify(seg)).join("|")
  }).join("::"), [nonIntradayGatherers])

  const cachedData = useStableContributorsCache(stableKey, data, isAnyQueryPending)
  const stableData = isAnyQueryPending ? cachedData : data

  const intradayPriceGatherers = useMemo(() => {
    return intradayGatherers.filter((gatherer) => gatherer.streamType === "prices")
  }, [intradayGatherers])
  const intradayStandaloneGatherers = useMemo(() => {
    return intradayGatherers.filter((gatherer) => gatherer.streamType === "standalone-performance")
  }, [intradayGatherers])

  const modelProviderIds = useMemo(() => {
    if (!isIntradayAnalyticsEnabled || !hasIntradayStreamingOrContributionGatherers || !stableData || !stableData.children) {
      return EMPTY_ARRAY
    }
    return collectLeafModelProviderIds(stableData)
  }, [stableData, isIntradayAnalyticsEnabled, hasIntradayStreamingOrContributionGatherers])

  const shouldEnableIntraday =
    isIntradayAnalyticsEnabled
    && hasIntradayStreamingOrContributionGatherers
    && !!stableData
    && modelProviderIds.length > 0
  const shouldSubscribeToPriceStream = shouldSubscribeToIntradayPriceTicks({
    intradayPriceGatherers,
    intradayContributionQueryGatherers,
  })
  const intradayStreamModelId =
    intradayPriceGatherers[0]?.modelId
    || getIntradayContributionGathererModelId(intradayContributionQueryGatherers[0])
    || intradayStandaloneGatherers[0]?.modelId

  const {
    prices: intradayPrices,
    lastUpdatedAt: intradayPricesLastUpdatedAt,
    updatedModelProviderIds: intradayUpdatedModelProviderIds,
  } = useIntradayPriceStream({
    modelProviderIds,
    modelId: intradayStreamModelId,
    enabled: shouldEnableIntraday && shouldSubscribeToPriceStream,
  })

  const standaloneSubscriptions = useMemo(() => {
    if (!shouldEnableIntraday || intradayStandaloneGatherers.length === 0) {
      return []
    }
    const periods = [...new Set(intradayStandaloneGatherers.map((gatherer) => gatherer.period).filter(Boolean))]
    return periods.map((period) => ({
      period,
      modelProviderIds,
    }))
  }, [intradayStandaloneGatherers, modelProviderIds, shouldEnableIntraday])

  const { standalonePerformanceByPeriod, lastUpdatedAt: standalonePerformanceLastUpdatedAt } = useIntradayStandalonePerformanceStream({
    subscriptions: standaloneSubscriptions,
    modelId: intradayStandaloneGatherers[0]?.modelId,
    enabled: shouldEnableIntraday && standaloneSubscriptions.length > 0,
  })

  const contributionQueryGatherersBySuffix = useMemo(() => {
    return intradayContributionQueryGatherers.reduce((gatherersBySuffix, gatherer) => {
      if (!shouldAutoInvalidateIntradayContributionGathererOnPricingUpdate(gatherer)) {
        return gatherersBySuffix
      }

      const suffix = getInstrumentSuffixForIntradayContributionPrefix(gatherer.prefix)
      if (suffix == null) {
        return gatherersBySuffix
      }

      if (!gatherersBySuffix[suffix]) {
        gatherersBySuffix[suffix] = []
      }
      gatherersBySuffix[suffix].push(gatherer)
      return gatherersBySuffix
    }, {})
  }, [intradayContributionQueryGatherers])
  const contributionSuffixIndexByModelProviderId = useMemo(() => {
    return buildContributionSuffixIndexByModelProviderId(stableData)
  }, [stableData])

  useEffect(() => {
    if (!shouldEnableIntraday) {
      return
    }
    if (!intradayContributionQueryGatherers.length || !intradayUpdatedModelProviderIds.length) {
      return
    }

    const allSuffixes = Object.keys(contributionQueryGatherersBySuffix)
    if (!allSuffixes.length) {
      return
    }

    const impactedSuffixes = new Set()
    for (const modelProviderId of intradayUpdatedModelProviderIds) {
      const normalizedModelProviderId = normalizeContributorKey(modelProviderId)
      const suffixes = contributionSuffixIndexByModelProviderId.get(modelProviderId)
        || contributionSuffixIndexByModelProviderId.get(normalizedModelProviderId)
      if (suffixes && suffixes.size > 0) {
        suffixes.forEach((suffix) => impactedSuffixes.add(suffix))
      }
    }

    const suffixesToInvalidate = impactedSuffixes.size > 0 ? [...impactedSuffixes] : allSuffixes
    const gatherersToInvalidate = suffixesToInvalidate.flatMap((suffix) => contributionQueryGatherersBySuffix[suffix] || EMPTY_ARRAY)
    if (!gatherersToInvalidate.length) {
      return
    }

    gatherersToInvalidate.forEach((gatherer) => {
      queryClient.invalidateQueries({
        queryKey: [gatherer.key],
        exact: true,
        refetchType: "active",
      })
    })
  }, [
    shouldEnableIntraday,
    intradayContributionQueryGatherers,
    intradayUpdatedModelProviderIds,
    contributionSuffixIndexByModelProviderId,
    contributionQueryGatherersBySuffix,
    queryClient,
  ])

  const dataWithIntraday = useMemo(() => {
    if (!isIntradayAnalyticsEnabled || !hasIntradayStreamingOrContributionGatherers || !stableData) {
      return stableData
    }
    if (isEmpty(intradayPrices) && isEmpty(standalonePerformanceByPeriod)) {
      return stableData
    }

    const intradayPricesByRowId = buildIntradayFlatMap(intradayPrices)
    const priceContributors = map(intradayPriceGatherers, (gatherer) => ({
      prefix: gatherer.prefix,
      valuesByRowId: intradayPricesByRowId,
    }))

    const standaloneFlatMapByPeriodKey = intradayStandaloneGatherers.reduce((flatMapCacheByPeriod, gatherer) => {
      const periodKey = String(gatherer.period)
      if (!flatMapCacheByPeriod.has(periodKey)) {
        flatMapCacheByPeriod.set(
          periodKey,
          buildIntradayStandaloneFlatMap(standalonePerformanceByPeriod, gatherer.period),
        )
      }
      return flatMapCacheByPeriod
    }, new Map())

    const standaloneContributors = map(intradayStandaloneGatherers, (gatherer) => ({
      prefix: gatherer.prefix,
      valuesByRowId: standaloneFlatMapByPeriodKey.get(String(gatherer.period)) || {},
    }))

    return mergeFlatContributors(stableData, [...priceContributors, ...standaloneContributors])
  }, [
    stableData,
    intradayPrices,
    intradayPriceGatherers,
    intradayStandaloneGatherers,
    standalonePerformanceByPeriod,
    isIntradayAnalyticsEnabled,
    hasIntradayStreamingOrContributionGatherers,
  ])

  // When we already have data from stableData, a pending status is a background refresh — suppress
  // the skeleton by returning OK so the existing rows remain visible.
  const effectiveStatus = useMemo(() => {
    if (status === LOADING && dataWithIntraday) {
      return OK
    }
    return status
  }, [status, dataWithIntraday])

  const metricsWithIntradayTooltip = useMemo(() => {
    if (!isIntradayAnalyticsEnabled || !hasIntradayStreamingOrContributionGatherers) {
      return metrics
    }

    return enrichMetricsWithIntradayAsOfTooltips(metrics, {
      intradayPricesLastUpdatedAt,
      intradayContributionPerformanceLastUpdatedAt,
      intradayStandalonePerformanceLastUpdatedAt: standalonePerformanceLastUpdatedAt,
    }, { instrumentsWithMetadata })
  }, [
    metrics,
    intradayPricesLastUpdatedAt,
    intradayContributionPerformanceLastUpdatedAt,
    standalonePerformanceLastUpdatedAt,
    isIntradayAnalyticsEnabled,
    hasIntradayStreamingOrContributionGatherers,
    instrumentsWithMetadata,
  ])

  return {
    data: dataWithIntraday,
    status: effectiveStatus,
    metrics: metricsWithIntradayTooltip,
    modelProviderIds,
    isIntradayContributionRefreshing: isFetchingIntradayContributionQueries && effectiveStatus === OK,
  }
}
